import { isAfter } from 'date-fns';
import { useLang } from 'hooks/lang';

import { projectStatusConfig, actions, projectStatusByCode } from 'entities';

import { CalendarIcon } from 'components/Icons';

import { useSingleJustification } from 'providers/Justification';
import { useToast } from 'providers/Toast';
import { useProjectProfile } from '../../context';

import { MESSAGES } from '../../utils';
import { getLocalDateString } from 'utils/date';

import { registerProjectOption } from '../options';

export default registerProjectOption({
  Icon: CalendarIcon,

  action: 'CHANGE_DATE',

  shortcut: {
    key: 'K',
    triggerKey: ['ctrl'],
  },

  useOption: ({ loggedUser, project }) => {
    const { updateProject } = useProjectProfile();

    const { addToast } = useToast();

    const { t } = useLang();

    const isLeader = loggedUser.type === 'LEADER';

    const config = projectStatusByCode[project.status];

    const canChangeDate =
      loggedUser.id === project?.owner.id && config.canChangeDate && !config.blocks.fullBlock;

    const changeDate = actions.projects.requestProjectDateChange.use({
      onSuccess: (_, { date }) => {
        updateProject((old) => ({
          ...old,
          status: isLeader ? old.status : projectStatusConfig.pendingApproval.code,
          expectedDate: isLeader ? date : old.expectedDate,
        }));

        addToast(MESSAGES[isLeader ? 'projectDateChanged' : 'dateChangeRequestSent'](t));
      },
    });

    const handleChangeDateClick = useSingleJustification({
      type: 'date',

      shouldDisableDate: (date: Date | null) => {
        if (!date) return false;

        return isAfter(new Date(project.expectedDate as string), date);
      },

      cancel: () => addToast(MESSAGES.projectDateChangeAborted(t)),

      text: `${t('why-is-this-change-necessary-pick-the-new-date')} ${
        loggedUser.type === 'LEADER'
          ? t('because-you-are-a-leader-this-change-will-be-executed-immediately')
          : ''
      }`,

      onConfirm: async (justification, date) => {
        if (!date) return;

        const newDate = getLocalDateString(date, { accountTimezone: true });

        if (newDate === project.expectedDate) return addToast(MESSAGES.projectDateChangeAborted(t));

        await changeDate.execute({
          project: project.id,
          justification,
          date: newDate,
        });
      },
    });

    return {
      action: handleChangeDateClick,
      isEnabled: canChangeDate,
      project,
      getLabel: () =>
        project?.expectedDateRequest ? t('date-change-requested') : t('change-expected-date'),
    };
  },
});
